import {
  Box,
  Button,
  Divider,
  Typography,
  Link as MUILink,
} from '@mui/material';
import React from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import Link from 'next/link';
import { Subscription } from '../../redux/types.generated';

interface CancelSusbscriptionDialogProps {
  activeSubscription: Subscription;
  setShowConfirmSubscriptionDialog: (isOpen: boolean) => void;
  onCancelSubscription: () => void;
}

const CancelSusbscriptionDialog: React.FC<CancelSusbscriptionDialogProps> = ({
  setShowConfirmSubscriptionDialog,
  activeSubscription,
  onCancelSubscription,
}) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const renderPaymentPolicy = () => (
    <Link
      href={'/refunds-and-cancellations'}
      key={'refunds-and-cancellations'}
      passHref
    >
      <MUILink
        target='_blank'
        variant={'subtitle1'}
        sx={styles.linkStyles}
        letterSpacing={0.6}
      >
        payments and refund policy
      </MUILink>
    </Link>
  );

  const renderExpirationDate = () => (
    <Typography
      variant='subtitle1'
      fontWeight={700}
      letterSpacing={0.6}
      color={theme.ragyaPalette.gray1}
      sx={[styles.subscriptionItemContainer, { display: 'inline' }]}
    >
      {format(new Date(activeSubscription?.expiresAt), 'do MMM yyyy')}
    </Typography>
  );
  return (
    <Box>
      <Typography
        variant='body1'
        fontWeight={700}
        sx={styles.subscriptionItemContainer}
      >
        😢 Really cancel your subscription?
      </Typography>
      <Typography
        variant='subtitle1'
        color={theme.ragyaPalette.gray1}
        letterSpacing={0.6}
        sx={[styles.subscriptionItemContainer]}
      >
        Your current subscription will stay active till {'  '}
        {renderExpirationDate()} and you won’t be billed any further. Please
        refer to our {renderPaymentPolicy()} for more details.
      </Typography>
      <Box sx={styles.subscriptionItemContainer}>
        <Button
          variant={'contained'}
          disableRipple
          disableFocusRipple
          disableTouchRipple
          sx={{
            textTransform: 'none',
            backgroundColor: theme.ragyaPalette.successGreen,
            '&:hover': { backgroundColor: theme.ragyaPalette.successGreen },
            mr: 1,
          }}
          onClick={() => setShowConfirmSubscriptionDialog(false)}
        >
          <Typography variant='body1' fontWeight={700}>
            No, stay premium
          </Typography>
        </Button>
        <Button
          variant={'contained'}
          disableRipple
          disableFocusRipple
          disableTouchRipple
          sx={{
            textTransform: 'none',
            backgroundColor: theme.ragyaPalette.errorRed,
            '&:hover': { backgroundColor: theme.ragyaPalette.errorRed },
          }}
          onClick={onCancelSubscription}
        >
          <Typography variant='body1' fontWeight={700} component={'span'}>
            Yes, cancel
          </Typography>
        </Button>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Typography
        variant='subtitle1'
        color={theme.ragyaPalette.gray4}
        sx={styles.subscriptionItemContainer}
      >
        We are sad to see you go and would really like to have you back soon. We
        will be grateful if you can share with us how we can improve.
      </Typography>
      <MUILink
        href='mailto:support@ragya.com'
        variant='inherit'
        sx={styles.linkStyles}
      >
        Send us feedback
      </MUILink>{' '}
    </Box>
  );
};

export default CancelSusbscriptionDialog;
const useStyles = (theme: Theme) => ({
  subscriptionItemContainer: { mb: 2 },
  successButton: {
    textTransform: 'none',
    backgroundColor: theme.ragyaPalette.successGreen,
    '&:hover': { backgroundColor: theme.ragyaPalette.successGreen },
    mr: 1,
  },
  dangerButton: {
    textTransform: 'none',
    backgroundColor: theme.ragyaPalette.errorRed,
    '&:hover': { backgroundColor: theme.ragyaPalette.errorRed },
  },
  linkStyles: {
    color: theme.ragyaPalette.focusBlue,
    textDecorationColor: theme.ragyaPalette.focusBlue,
  },
});
